import { useState, FormEvent, useMemo, useEffect } from "react";
import Form from "react-bootstrap/Form";
import { resources } from "@constants/hardcoded-strings";
import { MONTH_DAY_YEAR_INPUT_DATE_FORMAT } from "@constants/date-formats";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ButtonTypes } from "@enums/button-types";
import { TimeRangeTypes } from "@enums/time-range-types";
import { SelectOption } from "@idw-types/common/select-option";
import { Company } from "@idw-types/data/reference-data/generic";
import { OrganizationActivitySearchFiltersState } from "@idw-types/state/organization-activity-state";
import { getTimeRange, getTimeRangeLabel } from "@utils/date-util";
import { mapEntityWithNameAndIdToSelectOption } from "@utils/select-option-mapping-util";
import { SearchFormFilterRow } from "@containers/common/search-form-filter-row/search-form-filter-row";
import { TimeRangeSearchFormFilterRow } from "@containers/common/time-range-search-form-filter-row/time-range-search-form-filter-row";
import { OrganizationSelectFormGroup } from "@containers/common/select-form-groups";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import styles from "./activity-log-search-form.less";

type ActivityLogSearchFormProps = {
    requestInProgress: boolean;
    companies: Array<Company>;
    searchFilters: OrganizationActivitySearchFiltersState;
    handleSearch: (searchFilters: OrganizationActivitySearchFiltersState) => void;
};

type ActivityLogSearchFormState = {
    selectedCompanyOption: SelectOption;
    selectedTimeRange: SelectOption;
    startDate: Date;
    endDate: Date;
};

export const ActivityLogSearchForm = (props: ActivityLogSearchFormProps) => {
    const maxDate = useMemo(() => new Date(), []);
    const minDate = useMemo(() => new Date(2015, 0, 1), []);
    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const {
        requestInProgress,
        companies,
        searchFilters,
        handleSearch
    } = props;

    const getFilterStateFromSearchFilters = (searchFilters: OrganizationActivitySearchFiltersState): ActivityLogSearchFormState => {
        const selectedCompany = companies.find(company => searchFilters.companyId === company.id);

        return {
            selectedCompanyOption: selectedCompany ? mapEntityWithNameAndIdToSelectOption(selectedCompany) : null,
            selectedTimeRange: {
                label: getTimeRangeLabel(searchFilters.timeRangeType),
                value: searchFilters.timeRangeType
            },
            startDate: searchFilters.startDate,
            endDate: searchFilters.endDate
        };
    };

    const [filterState, setFilterState] = useState<ActivityLogSearchFormState>(getFilterStateFromSearchFilters(searchFilters));

    useEffect(() => {
        setFilterState(getFilterStateFromSearchFilters(searchFilters));
    }, [searchFilters]);

    const handleSubmit = (event: FormEvent) => {
        const companyId = filterState.selectedCompanyOption?.value || null;

        event.preventDefault();
        handleSearch({
            companyId,
            startDate: filterState.startDate,
            endDate: filterState.endDate,
            timeRangeType: filterState.selectedTimeRange.value as TimeRangeTypes
        });
    };

    const handleUpdateCompanyOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedCompanyOption: newValue
        });
    };

    const handleTimeRangeChange = (newValue: SelectOption) => {
        if (newValue.value === TimeRangeTypes.CUSTOM) {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue
            });
        } else {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue,
                ...getTimeRange(newValue.value as TimeRangeTypes)
            });
        }
    };

    const handleStartDateChange = (newStartDate: Date) => {
        setFilterState({
            ...filterState,
            startDate: newStartDate
        });
    };

    const handleEndDateChange = (newEndDate: Date) => {
        setFilterState({
            ...filterState,
            endDate: newEndDate
        });
    };

    const timeRangeTypes = [
        TimeRangeTypes.CURRENT_MONTH,
        TimeRangeTypes.LAST_MONTH,
        TimeRangeTypes.CURRENT_QUARTER,
        TimeRangeTypes.LAST_QUARTER,
        TimeRangeTypes.CURRENT_YEAR,
        TimeRangeTypes.LAST_YEAR,
        TimeRangeTypes.CUSTOM
    ];

    return (
        <Form className={styles.activityLogSearchForm} onSubmit={handleSubmit}>
            {companies.length > 1 && (
                <SearchFormFilterRow>
                    <OrganizationSelectFormGroup
                        idPrefix="activity-log"
                        axnCompanies={companies}
                        isClearable={true}
                        isRequired={false}
                        layout={layout}
                        isSearchable={true}
                        labelWidth={labelWidth}
                        selectedOption={filterState.selectedCompanyOption}
                        onChange={handleUpdateCompanyOption}
                        disabled={false}
                    />
                </SearchFormFilterRow>
            )}
            <TimeRangeSearchFormFilterRow
                dateFormat={MONTH_DAY_YEAR_INPUT_DATE_FORMAT}
                endDate={filterState.endDate}
                endDateLabel={resources.common.searchForm.filters.endDate.label}
                endDateAriaLabel={resources.common.searchForm.filters.endDate.label}
                startDate={filterState.startDate}
                startDateLabel={resources.common.searchForm.filters.startDate.label}
                startDateAriaLabel={resources.common.searchForm.filters.startDate.label}
                idPrefix={"activity-log"}
                labelWidth={labelWidth}
                layout={layout}
                minDate={minDate}
                maxDate={maxDate}
                onChangeEndDate={handleEndDateChange}
                onChangeStartDate={handleStartDateChange}
                onChangeTimeRange={handleTimeRangeChange}
                selectedTimeRange={filterState.selectedTimeRange}
                timeRangeTypes={timeRangeTypes}
                showMonthDropdown={true}
                showYearDropdown={true}
                showMonthYearPicker={false}
            />
            <div className={styles.buttonDiv}>
                <ButtonWithSpinner
                    id="activity-log-search-button"
                    disabled={requestInProgress}
                    hideSpinner={false}
                    variant="primary"
                    type={ButtonTypes.SUBMIT}
                    label={resources.common.buttons.search.label}
                />
            </div>
        </Form>
    );
};